import React, { useEffect, useState } from 'react';
import { setSelectedColor, setSelectedSku } from 'store/slices/quickView';
import {
  defaultColorSelector,
  defaultSkuPartNumberSelector,
} from 'store/selectors/quickViewSelector';
import { useAppSelector, useAppDispatch } from 'store/store';
import AttributeGroup from './AttributeGroup';
import ColorGroup from './ColorGroup';
import useAttributeEngine from '../../hooks/useAttributeEngine';
import { SkuDetails, StyleDetails } from '../FulfillmentOptions/types';

type AttributesProps = {
  productGroup: {
    skus: SkuDetails[];
    style: StyleDetails;
  };
  setColor: (color: string) => void;
  sku: SkuDetails;
  setSKU: (sku: SkuDetails) => void;
  validate: boolean;
  setValidate: () => void;
  skuDefaultValue: string;
  setAttributesState: (state: Record<string, string>) => void;
};

const Attributes = (props: AttributesProps) => {
  const {
    productGroup,
    setColor,
    sku,
    setSKU,
    validate,
    setValidate,
    skuDefaultValue,
    setAttributesState,
  } = props;

  const dispatch = useAppDispatch();

  // sku default value details are store here
  const [skuDefaultAttributes, setSkuDefaultAttributes] = useState(null);
  const [defaultsApplied, setDefaultsApplied] = useState(false);

  const defaultColor = useAppSelector(defaultColorSelector);
  const defaultSkuPartNumber = useAppSelector(defaultSkuPartNumberSelector);

  const [state, handleClick] = useAttributeEngine(productGroup);

  const { selected, initialEnabled, resolved, enabled } = state;

  useEffect(() => {
    if (skuDefaultValue !== '') {
      const skuValueObject = productGroup.skus.find((val) => val.partNumber === skuDefaultValue);
      setSkuDefaultAttributes(skuValueObject?.definingAttributes);
    }
  }, [skuDefaultValue]);

  useEffect(() => {
    setSkuDefaultAttributes(null);
    setDefaultsApplied(false);
  }, [productGroup?.style?.partNumber]);

  // Apply defaults after attribute engine is ready and enabled is populated
  // This ensures we only apply defaults once the engine is fully initialized
  // and the enabled array is available.
  // also prevents re-applying defaults on every render.
  useEffect(() => {
    if (!defaultsApplied && enabled && Object.keys(enabled).length > 0) {
      if (defaultColor && enabled.Color?.includes(defaultColor)) {
        handleClick('Color', defaultColor);
      }

      // Apply default SKU attributes if provided
      if (defaultSkuPartNumber && productGroup.skus) {
        const defaultSku = productGroup.skus.find((s) => s.partNumber === defaultSkuPartNumber);
        if (defaultSku?.definingAttributes) {
          defaultSku.definingAttributes.forEach((attr) => {
            if (enabled[attr.name]?.includes(attr.value)) {
              handleClick(attr.name, attr.value);
            }
          });
        }
      }

      setDefaultsApplied(true);
    }
  }, [enabled, defaultColor, defaultSkuPartNumber, defaultsApplied]);

  useEffect(() => {
    // Update local and Redux state when attribute engine selection changes
    if (selected.Color) {
      setColor(selected.Color);
      dispatch(setSelectedColor(selected.Color));
    }
  }, [selected.Color]);

  useEffect(() => {
    setAttributesState(selected);
  }, [selected]);

  useEffect(() => {
    const singleSKU =
      Object.keys(selected).length === Object.keys(initialEnabled).length ? resolved[0] : null;
    setSKU(singleSKU);
    if (singleSKU) {
      dispatch(setSelectedSku(singleSKU));
    }
  }, [resolved]);

  if (productGroup?.skus?.length === 1) {
    setSKU(productGroup.skus[0]);
    if (
      !productGroup.style.definingAttributes ||
      productGroup.style.definingAttributes.length === 0
    ) {
      return null;
    }
  }

  const getSKU = (attribute: string, value: string) => {
    let match = null;
    productGroup.skus.forEach((productSku: SkuDetails) => {
      if (productSku.definingAttributes) {
        productSku.definingAttributes.forEach((attr) => {
          if (attr.name === attribute && attr.value === value) {
            match = productSku;
          }
        });
      }
    });

    return match;
  };

  return (
    <div className="attribute-groups product-attributes hmf-display-flex hmf-flex-column">
      {productGroup.style.definingAttributes.map((attr) => {
        const values = attr.value.split('|');
        if (attr.name.toLowerCase() === 'color') {
          return (
            <ColorGroup
              partNumber={productGroup.style.partNumber}
              attribute={attr.name}
              key={attr.name}
              handleClick={(value) => {
                handleClick(attr.name, value);
              }}
              potential={values || []}
              selected={selected[attr.name] || ''}
              enabled={enabled[attr.name] || []}
              getSKU={(attribute, value) => getSKU(attribute, value)}
              validate={validate}
              setValidate={setValidate}
              sku={sku}
              skuDefaultAttributes={skuDefaultAttributes}
            />
          );
        }
        return (
          <AttributeGroup
            attribute={attr.name}
            key={attr.name}
            handleClick={(name, value) => {
              handleClick(name, value);
            }}
            potential={values || []}
            selected={selected[attr.name] || ''}
            enabled={enabled[attr.name] || []}
            validate={validate}
            setValidate={setValidate}
            sku={sku}
            skuDefaultAttributes={skuDefaultAttributes}
          />
        );
      })}
    </div>
  );
};

export default Attributes;
